import JumboContentLayout from "@jumbo/components/JumboContentLayout";
import { useAPI } from "app/hooks/useApi";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import HeaderPages from "../components/HeaderPages";
import { verifyAndRefreshToken, backupAuthInfo } from './AuthDebugger';
import { fetchEnvolvidos, processEnvolvidosData } from './EnvolvidosFetcher';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  IconButton,
  Paper,
  Avatar,
  Tooltip,
  Alert,
  Snackbar,
  Collapse,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Chip
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Looks3 as Looks3Icon,
  Info as InfoIcon,
  Home as HomeIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon
} from "@mui/icons-material";
import { Form, Formik } from "formik";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import Can from "../components/Can/Can";

const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

const createSchemaValidation = yup.object({
  name: yup.string().required("Campo obrigatório"),
  email: yup.string().email("Email inválido").required("Campo obrigatório"),
  password: yup.string().min(6, "Mínimo 6 caracteres").required("Campo obrigatório"),
  passwordConfirm: yup
    .string()
    .oneOf([yup.ref("password"), null], "As senhas devem ser iguais")
    .required("Preenchimento obrigatório"),
});

const updateSchemaValidation = yup.object({
  name: yup.string().required("Campo obrigatório"),
  email: yup.string().email("Email inválido").required("Campo obrigatório"),
  password: yup.string().optional(),
  passwordConfirm: yup
    .string()
    .oneOf([yup.ref("password"), null], "As senhas devem ser iguais")
    .when("password", {
      is: (password) => !!password,
      then: (schema) => schema.required("Preenchimento obrigatório"),
    }),
});

const initialState = {
  usuario: {
    name: "",
    email: "",
    password: "",
    passwordConfirm: "",
    role: "",
  },
  envolvidos: {
    options: [],
    filtered: [],
    selected: null,
    loading: false,
  },
  processos: {
    envolvido: [],
    responsavel: [],
    cache: {},
    loading: false,
    shouldRefresh: false,
  },
  permissoes: {
    available: [], // Todas as permissões disponíveis
    user: [], // Permissões do usuário atual
    loading: false,
    saving: false,
    changes: {} // Track de mudanças pendentes
  },
  emailNotifications: {
    available: [], // Todos os tipos de notificação disponíveis
    user: [], // Notificações do usuário atual
    loading: false,
    saving: false,
    changes: {} // Track de mudanças pendentes
  },
  observacaoEnvolvidos: {
    envolvidos: [], // Envolvidos que o usuário está observando
    selectedEnvolvido: null, // Envolvido selecionado para adicionar
    loading: false,
    saving: false,
  },
  ui: {
    associando: false,
    confirmDialogOpen: false,
    confirmRemoveDialogOpen: false,
    processoToRemove: null,
    message: null,
    messageType: 'info',
    permissionsTabOpen: false,
    emailNotificationsTabOpen: false,
    observacaoEnvolvidosTabOpen: false,
  }
};

const UsuarioForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setAuthOptions, authUser } = useJumboAuth();
  const { makeRequest } = useAPI();
  
  const [state, setState] = useState(initialState);
  
  const isAdmin = useMemo(() => authUser?.role === "ADMIN", [authUser?.role]);
  const isEditing = useMemo(() => !!id, [id]);
  const canManageProcesses = useMemo(() => 
    isEditing && (state.usuario.role === 'OPERADOR' || state.usuario.role === 'ADMIN'), 
    [isEditing, state.usuario.role]
  );
  const isViewerUser = useMemo(() => 
    isEditing && (state.usuario.role === 'CLIENTE' || state.usuario.role === 'VIEWER'), 
    [isEditing, state.usuario.role]
  );

  const debugLog = useCallback((message, data) => {
    if (IS_DEVELOPMENT) {

    }
  }, []);
  const updateState = useCallback((updates) => {
    setState(prev => {
      const newState = { ...prev };
      Object.keys(updates).forEach(key => {
        if (typeof updates[key] === 'object' && updates[key] !== null && !Array.isArray(updates[key])) {
          newState[key] = { ...prev[key], ...updates[key] };
        } else {
          newState[key] = updates[key];
        }
      });
      return newState;
    });
  }, []); 
  const showMessage = useCallback((message, type = 'info') => {
    setState(prev => {
      if (prev.ui.message === message && prev.ui.messageType === type) {
        return prev; 
      }
      return {
        ...prev,
        ui: {
          ...prev.ui,
          message,
          messageType: type
        }
      };
    });
  }, []); 
  const getUsuario = useCallback(async () => {
    if (!id) return;
    
    try {
      const response = await makeRequest(`/users/${id}`, "GET");
      if (response.status === 200) {
        setState(prev => ({
          ...prev,
          usuario: response.data
        }));
      } else {
        throw new Error('Não foi possível carregar os dados do usuário');
      }
    } catch (error) {
      debugLog('Erro ao buscar usuário:', error);
      showMessage('Erro ao carregar dados do usuário', 'error');
      setState(prev => ({
        ...prev,
        usuario: initialState.usuario
      }));
    }
  }, [id, makeRequest, showMessage, debugLog]);

  const salvarUsuario = useCallback(async (values) => {
    const userData = { ...values };
    delete userData.passwordConfirm;
    delete userData.createdAt;
    delete userData.updatedAt;
    delete userData.deletedAt;
    delete userData.id;
    
    try {
      const response = await makeRequest(
        `/users/${id || ""}`,
        id ? "PUT" : "POST",
        userData
      );

      if ([200, 201].includes(response.status)) {
        return response;
      } else {
        throw new Error('Erro ao salvar usuário');
      }
    } catch (error) {
      debugLog('Erro ao salvar usuário:', error);
      return null;
    }
  }, [id, makeRequest, debugLog]);

  const handleSubmit = useCallback(async (values) => {
    const response = await salvarUsuario(values);
    if (response) {
      if (id && authUser?.id === parseInt(id, 10)) {
        setAuthOptions((prevAuthOptions) => ({
          ...prevAuthOptions,
          payload: {
            ...prevAuthOptions.payload,
            authUser: {
              ...prevAuthOptions.payload?.authUser,
              name: values.name,
            },
          },
        }));
      }
      
      showMessage('Usuário salvo com sucesso!', 'success');
      setTimeout(() => navigate("/usuarios"), 1500);
    } else {
      showMessage('Erro ao salvar usuário', 'error');
    }
  }, [salvarUsuario, id, authUser, setAuthOptions, showMessage, navigate]);

  const normalizeEnvolvidoData = useCallback((envolvido) => {
    const result = {
      ...envolvido,
      // Garantir que sempre tenha um campo 'nome' para compatibilidade
      nome: envolvido.nome || envolvido.nome_razao_social || envolvido.identificacao || envolvido.nome_identificacao || '',
      displayName: envolvido.nome_razao_social || envolvido.identificacao || envolvido.nome_identificacao || '',
      searchText: `${envolvido.nome_razao_social || envolvido.identificacao || ''} ${envolvido.cpf_cnpj || ''} ${envolvido.id || ''} ${envolvido.email || ''}`.toLowerCase()
    };
    
    if (IS_DEVELOPMENT && isViewerUser) {

    }
    
    return result;
  }, [isViewerUser]);
  const silentRequest = useCallback(async (url, method = "GET") => {
    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      debugLog('Token para autenticação:', token ? 'Existe (primeiros caracteres: ' + token.substring(0, 5) + '...)' : 'Não existe');
      
      if (!token) {
        debugLog('Token não encontrado no localStorage - isso causará erro 401');
        showMessage('Sessão expirada. Por favor, faça login novamente.', 'warning');
      }
      
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      debugLog('Headers da requisição:', headers);
      
      const response = await fetch(`${baseUrl}${url}`, {
        method,
        headers,
        credentials: 'include' 
      });
      
      debugLog(`Resposta da API ${url}:`, {
        status: response.status,
        statusText: response.statusText,
        headers: Array.from(response.headers.entries())
      });
      
      if (response.ok) {
        return {
          status: response.status,
          data: await response.json()
        };
      }
      
      if (response.status === 401) {
        debugLog('Erro de autenticação (401) na requisição:', url);
      }
      
      return {
        status: response.status,
        data: null
      };
    } catch (error) {
      debugLog(`Erro silencioso na requisição ${url}:`, error);
      return {
        status: 0,
        data: null
      };
    }
  }, [debugLog]);
  const buscarEnvolvidos = useCallback(async () => {
    setState(prev => ({
      ...prev,
      envolvidos: {
        ...prev.envolvidos,
        loading: true
      }
    }));
    
    const token = localStorage.getItem('token');
    debugLog('Token disponível:', token ? 'Sim' : 'Não');
    
    if (!token) {
      const tokenRestored = verifyAndRefreshToken();
      debugLog('Tentativa de restauração de token:', tokenRestored ? 'Sucesso' : 'Falha');
      
      if (!tokenRestored) {
        debugLog('ALERTA: Token não encontrado no localStorage - isso causará erros de autenticação');
        showMessage('Sessão expirada. Por favor, faça login novamente.', 'error');
      }
    }
    
    const result = await fetchEnvolvidos(debugLog, showMessage);
    
    if (result.success) {
      debugLog('Fetch de envolvidos bem-sucedido:', result.message);
      
      const { dadosNormalizados, cache } = processEnvolvidosData(result.data, normalizeEnvolvidoData);
      
      setState(prev => ({
        ...prev,
        envolvidos: {
          ...prev.envolvidos,
          options: dadosNormalizados,
          filtered: dadosNormalizados,
          loading: false
        },
        processos: {
          ...prev.processos,
          cache
        }
      }));
      
      debugLog(`Carregados ${dadosNormalizados.length} envolvidos`);
      
      backupAuthInfo();
    } else {
      setState(prev => ({
        ...prev,
        envolvidos: {
          ...prev.envolvidos,
          options: [],
          filtered: [],
          loading: false
        }
      }));
      
      debugLog('Não foram encontrados envolvidos:', result.message);
      
      try {
        debugLog('Tentando uma última alternativa com makeRequest diretamente');
        const fallbackResponse = await makeRequest('/envolvidos', 'GET');
        
        if (fallbackResponse?.status === 200 && Array.isArray(fallbackResponse.data)) {
          const dados = fallbackResponse.data;
          const dadosNormalizados = dados.map(normalizeEnvolvidoData);
          
          const cache = {};
          
          setState(prev => ({
            ...prev,
            envolvidos: {
              ...prev.envolvidos,
              options: dadosNormalizados,
              filtered: dadosNormalizados,
              loading: false
            },
            processos: {
              ...prev.processos,
              cache
            }
          }));
            debugLog(`Fallback: Carregados ${dadosNormalizados.length} envolvidos (sem processos)`);
        }
      } catch (fallbackError) {
        debugLog('Falha no fallback:', fallbackError);
      }    }
  }, [debugLog, makeRequest, normalizeEnvolvidoData, showMessage, verifyAndRefreshToken, backupAuthInfo]);
  const filtrarEnvolvidos = useCallback((termo) => {
    if (IS_DEVELOPMENT) {

    }
    
    if (!termo.trim()) {
      setState(prev => ({
        ...prev,
        envolvidos: {
          ...prev.envolvidos,
          filtered: prev.envolvidos.options
        }
      }));
      return;
    }
    
    const termoLower = termo.toLowerCase();
    const filtered = state.envolvidos.options.filter(env => 
      env.searchText.includes(termoLower)
    );
    
    if (IS_DEVELOPMENT) {

    }
    
    setState(prev => ({
      ...prev,
      envolvidos: {
        ...prev.envolvidos,
        filtered
      }
    }));
  }, [state.envolvidos.options]);  
  const normalizarMarcaProcesso = useCallback((processo) => {
    const processoCopy = { ...processo };
    
    let marcaNome = '-';
    
    if (Array.isArray(processo.marca) && processo.marca.length > 0) {
      marcaNome = processo.marca[0].nome || '-';
      processoCopy.marca = { nome: marcaNome };
    } else if (processo.marca && typeof processo.marca === 'object') {
      if (!processo.marca.nome) {
        marcaNome = processo.marca.descricao || processo.marca.texto || '-';
        processoCopy.marca = { nome: marcaNome };
      }
    } else if (typeof processo.marca === 'string') {
      marcaNome = processo.marca;
      processoCopy.marca = { nome: marcaNome };
    } else if (!processo.marca) {
      processoCopy.marca = { nome: '-' };
    }
    
    return processoCopy;
  }, []);
  const buscarProcessosResponsavel = useCallback(async () => {
    if (!id) return;
    
    debugLog('Iniciando busca de processos responsável para usuário ID:', id);
    
    setState(prev => ({
      ...prev,
      processos: {
        ...prev.processos,
        loading: true
      }
    }));
    
    try {      
      const timestamp = new Date().getTime();
      const response = await makeRequest(
        `/processos-revistas-rpi?usuario_id=${id}&page=0&perPage=100&_=${timestamp}&forceRefresh=true`, 
        'GET',
        null,
        {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        }
      );
      
      let processos = response.status === 200 && response.data?.processos 
        ? response.data.processos 
        : response.status === 200 && Array.isArray(response.data)
        ? response.data 
        : [];
      
      debugLog('Processos recebidos (pré-normalização):', processos);
      
      processos = processos.map(normalizarMarcaProcesso);
      
      debugLog('Processos normalizados:', processos);
      
      setState(prev => ({
        ...prev,
        processos: {
          ...prev.processos,
          responsavel: processos,
          loading: false
        }
      }));
      
      debugLog(`Atualização concluída: ${processos.length} processos carregados`);
      
      return processos;
    } catch (error) {
      debugLog('Erro ao buscar processos responsável:', error);
      setState(prev => ({
        ...prev,
        processos: {
          ...prev.processos,
          responsavel: [],
          loading: false
        }
      }));
      return [];
    }
  }, [id, makeRequest, debugLog, normalizarMarcaProcesso]);
  const handleAssociarProcessos = useCallback(async () => {
    if (!state.envolvidos.selected || !id) {
      debugLog("Seleção de envolvido inválida ou ID não disponível", { 
        selected: state.envolvidos.selected,
        id 
      });
      return;
    }
    
    let processosEnvolvido = state.processos.cache[state.envolvidos.selected.id] || [];
    
    processosEnvolvido = processosEnvolvido.map(normalizarMarcaProcesso);
    
    debugLog("Processos do envolvido selecionado:", processosEnvolvido);
    
    setState(prev => ({
      ...prev,
      processos: {
        ...prev.processos,
        envolvido: processosEnvolvido
      },
      ui: {
        ...prev.ui,
        confirmDialogOpen: true
      }
    }));
  }, [state.envolvidos.selected, state.processos.cache, id, debugLog, normalizarMarcaProcesso]); 
  const handleConfirmAssociar = useCallback(async () => {
    setState(prev => ({
        ...prev,
        ui: {
            ...prev.ui,
            associando: true,
            confirmDialogOpen: false
        }
    }));

    try {
        const userId = Number(id);
        if (isNaN(userId) || userId <= 0) {
            throw new Error('ID de usuário inválido');
        }        const response = await makeRequest('/users/associar-processos-envolvido', 'POST', {
            userId: userId,
            envolvidoId: Number(state.envolvidos.selected.id)
        });
        if ([200, 201].includes(response.status)) {
            const message = response.data?.message || '';
            const isSuccessMessage = message.includes('associados') || message.includes('sucesso') || !message;
            
            if (isSuccessMessage) {
                showMessage(message || 'Processos associados com sucesso!', 'success');
            } else {
                showMessage(message, 'info');
            }
            
            setState(prev => ({
                ...prev,
                ui: {
                    ...prev.ui,
                    associando: false,
                    confirmDialogOpen: false
                },
                envolvidos: {
                    ...prev.envolvidos,
                    selected: null
                },
                processos: {
                    ...prev.processos,
                    loading: true,
                    responsavel: [] 
                }
            }));
            
            const processos = await makeRequest(
                `/processos-revistas-rpi?usuario_id=${userId}&timestamp=${new Date().getTime()}`,
                'GET'
            );
            
            if (processos.status === 200) {
                const processosNormalizados = (processos.data?.processos || processos.data || [])
                    .map(normalizarMarcaProcesso);
                
                setState(prev => ({
                    ...prev,
                    processos: {
                        ...prev.processos,
                        loading: false,
                        responsavel: processosNormalizados
                    }
                }));
            }

        } else {
            debugLog('Status da resposta não é sucesso:', {
                status: response.status,
                message: response.data?.message,
                fullResponse: response
            });
            
            const message = response.data?.message || 'Erro ao associar processos';
            
            if (message.includes('processos associados') || message.includes('associados ao usuário')) {
                debugLog('Mensagem parece ser de sucesso mas veio com status de erro, tratando como informação');
                showMessage(message, 'info');
                
                setState(prev => ({
                    ...prev,
                    ui: {
                        ...prev.ui,
                        associando: false,
                        confirmDialogOpen: false
                    },
                    envolvidos: {
                        ...prev.envolvidos,
                        selected: null
                    }
                }));
                
                await buscarProcessosResponsavel();
            } else {
                throw new Error(message);
            }
        }
    } catch (error) {
        debugLog('Erro ao associar processos:', error);
        showMessage(error.message || 'Erro ao associar processos', 'error');
    } finally {
        setState(prev => ({
            ...prev,
            ui: {
                ...prev.ui,
                associando: false
            }
        }));
    }
}, [id, state.envolvidos.selected, state.processos.envolvido, makeRequest, showMessage, debugLog, buscarProcessosResponsavel]);
  const handleRemoverResponsavel = useCallback(async () => {
    if (!state.ui.processoToRemove) return;
    
    try {
      const response = await makeRequest(
        `/processos-revistas-rpi/${state.ui.processoToRemove.id}/remover-responsavel`, 
        'PUT'
      );
      
      if (response.status === 200) {
        setState(prevState => ({
          ...prevState,
          processos: {
            ...prevState.processos,
            responsavel: prevState.processos.responsavel.filter(
              p => p.id !== prevState.ui.processoToRemove.id
            )
          },
          ui: {
            ...prevState.ui,
            confirmRemoveDialogOpen: false,
            processoToRemove: null
          }
        }));
        showMessage('Responsável removido com sucesso', 'success');
      }
    } catch (error) {
      debugLog('Erro ao remover responsável:', error);
      showMessage('Erro ao remover responsável', 'error');
    }
  }, [state.ui.processoToRemove, makeRequest, showMessage, debugLog]);

  // ===== PERMISSÕES - FUNÇÕES DA API =====

  const carregarPermissoes = useCallback(async () => {
    setState(prev => ({
      ...prev,
      permissoes: {
        ...prev.permissoes,
        loading: true
      }
    }));

    try {
      if (IS_DEVELOPMENT) {

      }
      const response = await makeRequest('/users/permissions', 'GET');
      
      if (response.status === 200) {
        const permissions = response.data.permissions || [];
        setState(prev => ({
          ...prev,
          permissoes: {
            ...prev.permissoes,
            available: permissions,
            loading: false
          }
        }));
        if (IS_DEVELOPMENT) {

        }
      } else {
        throw new Error(`Erro ${response.status}: ${response.data?.message || 'Erro ao carregar permissões'}`);
      }
    } catch (error) {
      if (IS_DEVELOPMENT) {

      }
      setState(prev => ({
        ...prev,
        ui: { ...prev.ui, message: 'Erro ao carregar permissões do servidor', messageType: 'error' },
        permissoes: {
          ...prev.permissoes,
          available: [],
          loading: false
        }
      }));
    }
  }, [makeRequest]);

  const carregarPermissoesUsuario = useCallback(async () => {
    if (!id) return;

    setState(prev => ({
      ...prev,
      permissoes: {
        ...prev.permissoes,
        loading: true
      }
    }));

    try {
      if (IS_DEVELOPMENT) {

      }
      const response = await makeRequest(`/users/${id}/permissions`, 'GET');
      
      if (response.status === 200) {
        const userPermissions = response.data || [];
        setState(prev => ({
          ...prev,
          permissoes: {
            ...prev.permissoes,
            user: userPermissions,
            loading: false,
            changes: {}
          }
        }));
        if (IS_DEVELOPMENT) {

        }
      } else {
        throw new Error(`Erro ${response.status}: ${response.data?.message || 'Erro ao carregar permissões do usuário'}`);
      }
    } catch (error) {
      if (IS_DEVELOPMENT) {

      }
      setState(prev => ({
        ...prev,
        ui: { ...prev.ui, message: 'Erro ao carregar permissões do usuário', messageType: 'error' },
        permissoes: {
          ...prev.permissoes,
          user: [],
          loading: false
        }
      }));
    }
  }, [id, makeRequest]);

  const togglePermissao = useCallback((permissionId, granted) => {
    setState(prev => {
      const newChanges = { ...prev.permissoes.changes };
      newChanges[permissionId] = granted;

      return {
        ...prev,
        permissoes: {
          ...prev.permissoes,
          changes: newChanges
        }
      };
    });

    if (IS_DEVELOPMENT) {

    }
  }, []);

  const salvarPermissoes = useCallback(async () => {
    setState(prev => {
      if (Object.keys(prev.permissoes.changes).length === 0) {
        return {
          ...prev,
          ui: { ...prev.ui, message: 'Nenhuma alteração de permissão para salvar', messageType: 'info' }
        };
      }
      return {
        ...prev,
        permissoes: {
          ...prev.permissoes,
          saving: true
        }
      };
    });

    try {
      if (IS_DEVELOPMENT) {

      }
      
      // Usar setState callback para acessar o estado atual
      setState(prev => {
        if (Object.keys(prev.permissoes.changes).length === 0) {
          return prev;
        }

        // Calcular permissões finais baseado no estado atual + mudanças
        const currentPermissionIds = prev.permissoes.user.filter(up => up.granted).map(up => up.permission.id);
        const finalPermissions = [...currentPermissionIds];
        
        // Aplicar mudanças
        Object.entries(prev.permissoes.changes).forEach(([permId, granted]) => {
          const permissionId = parseInt(permId);
          if (granted) {
            if (!finalPermissions.includes(permissionId)) {
              finalPermissions.push(permissionId);
            }
          } else {
            const index = finalPermissions.indexOf(permissionId);
            if (index > -1) {
              finalPermissions.splice(index, 1);
            }
          }
        });

        if (IS_DEVELOPMENT) {

        }

        // Fazer a requisição
        makeRequest(`/users/${id}/permissions`, 'PUT', {
          permissionIds: finalPermissions
        }).then(response => {
          if (response.status === 200) {
            const updatedPermissions = response.data.permissions || [];
            setState(prevState => ({
              ...prevState,
              permissoes: {
                ...prevState.permissoes,
                user: updatedPermissions,
                changes: {},
                saving: false
              },
              ui: { ...prevState.ui, message: 'Permissões salvas com sucesso!', messageType: 'success' }
            }));
            
            if (IS_DEVELOPMENT) {

            }
          } else {
            throw new Error(`Erro ${response.status}: ${response.data?.message || 'Erro ao salvar permissões'}`);
          }
        }).catch(error => {
          if (IS_DEVELOPMENT) {

          }
          setState(prevState => ({
            ...prevState,
            permissoes: {
              ...prevState.permissoes,
              saving: false
            },
            ui: { ...prevState.ui, message: `Erro ao salvar permissões: ${error.message}`, messageType: 'error' }
          }));
        });

        return prev;
      });

    } catch (error) {
      if (IS_DEVELOPMENT) {

      }
      setState(prev => ({
        ...prev,
        permissoes: {
          ...prev.permissoes,
          saving: false
        },
        ui: { ...prev.ui, message: `Erro ao salvar permissões: ${error.message}`, messageType: 'error' }
      }));
    }
  }, [id, makeRequest]);

  const cancelarAlteracoes = useCallback(() => {
    setState(prev => ({
      ...prev,
      permissoes: {
        ...prev.permissoes,
        changes: {}
      },
      ui: { ...prev.ui, message: 'Alterações canceladas', messageType: 'info' }
    }));
  }, []);

  const getPermissionsByModule = useCallback(() => {
    const modules = {};
    state.permissoes.available.forEach(permission => {
      if (!modules[permission.module]) {
        modules[permission.module] = [];
      }
      modules[permission.module].push(permission);
    });
    return modules;
  }, [state.permissoes.available]);

  const isPermissionGranted = useCallback((permissionId) => {
    // Verificar se há mudanças pendentes
    if (state.permissoes.changes.hasOwnProperty(permissionId)) {
      return state.permissoes.changes[permissionId];
    }
    // Verificar estado atual da API
    return state.permissoes.user.some(up => up.permission && up.permission.id === permissionId && up.granted);
  }, [state.permissoes.user, state.permissoes.changes]);

  // ===== EMAIL NOTIFICATIONS - FUNÇÕES DA API =====

  const carregarTiposNotificacao = useCallback(async () => {
    setState(prev => ({
      ...prev,
      emailNotifications: {
        ...prev.emailNotifications,
        loading: true
      }
    }));

    try {
      if (IS_DEVELOPMENT) {

      }
      const response = await makeRequest('/users/email-notification-types', 'GET');
      
      if (response.status === 200) {
        const notificationTypes = response.data.notificationTypes || [];
        setState(prev => ({
          ...prev,
          emailNotifications: {
            ...prev.emailNotifications,
            available: notificationTypes,
            loading: false
          }
        }));
        if (IS_DEVELOPMENT) {

        }
      } else {
        throw new Error(`Erro ${response.status}: ${response.data?.message || 'Erro ao carregar tipos de notificação'}`);
      }
    } catch (error) {
      if (IS_DEVELOPMENT) {

      }
      setState(prev => ({
        ...prev,
        ui: { ...prev.ui, message: 'Erro ao carregar tipos de notificação do servidor', messageType: 'error' },
        emailNotifications: {
          ...prev.emailNotifications,
          available: [],
          loading: false
        }
      }));
    }
  }, [makeRequest]);

  const carregarNotificacoesUsuario = useCallback(async () => {
    if (!id) return;

    setState(prev => ({
      ...prev,
      emailNotifications: {
        ...prev.emailNotifications,
        loading: true
      }
    }));

    try {
      if (IS_DEVELOPMENT) {

      }
      const response = await makeRequest(`/users/${id}/email-notifications`, 'GET');
      
      if (response.status === 200) {
        const userNotifications = response.data || [];
        setState(prev => ({
          ...prev,
          emailNotifications: {
            ...prev.emailNotifications,
            user: userNotifications,
            loading: false,
            changes: {}
          }
        }));
        if (IS_DEVELOPMENT) {

        }
      } else {
        throw new Error(`Erro ${response.status}: ${response.data?.message || 'Erro ao carregar notificações do usuário'}`);
      }
    } catch (error) {
      if (IS_DEVELOPMENT) {

      }
      setState(prev => ({
        ...prev,
        ui: { ...prev.ui, message: 'Erro ao carregar notificações do usuário', messageType: 'error' },
        emailNotifications: {
          ...prev.emailNotifications,
          user: [],
          loading: false
        }
      }));
    }
  }, [id, makeRequest]);

  const toggleNotificacao = useCallback((notificationTypeId, enabled) => {
    setState(prev => {
      const newChanges = { ...prev.emailNotifications.changes };
      newChanges[notificationTypeId] = enabled;

      return {
        ...prev,
        emailNotifications: {
          ...prev.emailNotifications,
          changes: newChanges
        }
      };
    });

    if (IS_DEVELOPMENT) {

    }
  }, []);

  const salvarNotificacoes = useCallback(async () => {
    setState(prev => {
      if (Object.keys(prev.emailNotifications.changes).length === 0) {
        return {
          ...prev,
          ui: { ...prev.ui, message: 'Nenhuma alteração de notificação para salvar', messageType: 'info' }
        };
      }
      return {
        ...prev,
        emailNotifications: {
          ...prev.emailNotifications,
          saving: true
        }
      };
    });

    try {
      if (IS_DEVELOPMENT) {

      }
      
      setState(prev => {
        if (Object.keys(prev.emailNotifications.changes).length === 0) {
          return prev;
        }

        // Calcular notificações finais baseado no estado atual + mudanças
        const currentNotificationIds = prev.emailNotifications.user.map(un => un.email_notification_type.id);
        const finalNotifications = [...currentNotificationIds];
        
        // Aplicar mudanças
        Object.entries(prev.emailNotifications.changes).forEach(([notifId, enabled]) => {
          const notificationId = parseInt(notifId);
          if (enabled) {
            if (!finalNotifications.includes(notificationId)) {
              finalNotifications.push(notificationId);
            }
          } else {
            const index = finalNotifications.indexOf(notificationId);
            if (index > -1) {
              finalNotifications.splice(index, 1);
            }
          }
        });

        if (IS_DEVELOPMENT) {

        }

        // Fazer a requisição
        makeRequest(`/users/${id}/email-notifications`, 'PUT', {
          notificationTypeIds: finalNotifications
        }).then(response => {
          if (response.status === 200) {
            const updatedNotifications = response.data.notifications || [];
            setState(prevState => ({
              ...prevState,
              emailNotifications: {
                ...prevState.emailNotifications,
                user: updatedNotifications,
                changes: {},
                saving: false
              },
              ui: { ...prevState.ui, message: 'Notificações salvas com sucesso!', messageType: 'success' }
            }));
            
            if (IS_DEVELOPMENT) {

            }
          } else {
            throw new Error(`Erro ${response.status}: ${response.data?.message || 'Erro ao salvar notificações'}`);
          }
        }).catch(error => {
          if (IS_DEVELOPMENT) {

          }
          setState(prevState => ({
            ...prevState,
            emailNotifications: {
              ...prevState.emailNotifications,
              saving: false
            },
            ui: { ...prevState.ui, message: `Erro ao salvar notificações: ${error.message}`, messageType: 'error' }
          }));
        });

        return prev;
      });

    } catch (error) {
      if (IS_DEVELOPMENT) {

      }
      setState(prev => ({
        ...prev,
        emailNotifications: {
          ...prev.emailNotifications,
          saving: false
        },
        ui: { ...prev.ui, message: `Erro ao salvar notificações: ${error.message}`, messageType: 'error' }
      }));
    }
  }, [id, makeRequest]);

  const cancelarAlteracoesNotificacoes = useCallback(() => {
    setState(prev => ({
      ...prev,
      emailNotifications: {
        ...prev.emailNotifications,
        changes: {}
      },
      ui: { ...prev.ui, message: 'Alterações de notificação canceladas', messageType: 'info' }
    }));
  }, []);

  const getNotificationsByCategory = useCallback(() => {
    const categories = {};
    state.emailNotifications.available.forEach(notification => {
      if (!categories[notification.category]) {
        categories[notification.category] = [];
      }
      categories[notification.category].push(notification);
    });
    return categories;
  }, [state.emailNotifications.available]);

  const isNotificationEnabled = useCallback((notificationTypeId) => {
    // Verificar se há mudanças pendentes
    if (state.emailNotifications.changes.hasOwnProperty(notificationTypeId)) {
      return state.emailNotifications.changes[notificationTypeId];
    }
    // Verificar estado atual da API
    return state.emailNotifications.user.some(un => un.email_notification_type && un.email_notification_type.id === notificationTypeId && un.enabled);
  }, [state.emailNotifications.user, state.emailNotifications.changes]);

  // ===== OBSERVAÇÃO DE ENVOLVIDOS - FUNÇÕES DA API =====

  const carregarEnvolvidosObservados = useCallback(async () => {
    if (!id || !isViewerUser) return;

    setState(prev => ({
      ...prev,
      observacaoEnvolvidos: {
        ...prev.observacaoEnvolvidos,
        loading: true
      }
    }));

    try {
      if (IS_DEVELOPMENT) {

      }
      const response = await makeRequest(`/users/${id}/observando-envolvidos`, 'GET');
      
      if (response.status === 200) {
        const envolvidosObservados = Array.isArray(response.data?.data) ? response.data.data : [];
        setState(prev => ({
          ...prev,
          observacaoEnvolvidos: {
            ...prev.observacaoEnvolvidos,
            envolvidos: envolvidosObservados,
            loading: false
          }
        }));
        if (IS_DEVELOPMENT) {

        }
      } else {
        throw new Error(`Erro ${response.status}: ${response.data?.message || 'Erro ao carregar envolvidos observados'}`);
      }
    } catch (error) {
      // Não exibir erro para o usuário se a rota não existir
      setState(prev => ({
        ...prev,
        observacaoEnvolvidos: {
          ...prev.observacaoEnvolvidos,
          envolvidos: [],
          loading: false
        }
      }));
    }
  }, [id, makeRequest, isViewerUser]);

  const pararObservarEnvolvido = useCallback(async (envolvidoId) => {
    if (!id || !envolvidoId) return;

    setState(prev => ({
      ...prev,
      observacaoEnvolvidos: {
        ...prev.observacaoEnvolvidos,
        saving: true
      }
    }));

    try {
      if (IS_DEVELOPMENT) {

      }
      
      const response = await makeRequest(`/users/${id}/parar-observar-envolvido/${envolvidoId}`, 'DELETE');
      
      if (response.status === 200) {
        setState(prev => ({
          ...prev,
          observacaoEnvolvidos: {
            ...prev.observacaoEnvolvidos,
            envolvidos: (prev.observacaoEnvolvidos.envolvidos || []).filter(env => env.id !== envolvidoId),
            saving: false
          },
          ui: { ...prev.ui, message: 'Observação removida com sucesso!', messageType: 'success' }
        }));
        if (IS_DEVELOPMENT) {

        }
      } else {
        throw new Error(`Erro ${response.status}: ${response.data?.message || 'Erro ao parar observação'}`);
      }
    } catch (error) {
      if (IS_DEVELOPMENT) {

      }
      setState(prev => ({
        ...prev,
        ui: { ...prev.ui, message: 'Erro ao parar observação do envolvido', messageType: 'error' },
        observacaoEnvolvidos: {
          ...prev.observacaoEnvolvidos,
          saving: false
        }
      }));
    }
  }, [id, makeRequest]);

  const adicionarObservacaoEnvolvido = useCallback(async (envolvidoId) => {
    if (!id || !envolvidoId) {
      if (IS_DEVELOPMENT) {

      }
      return;
    }

    // Validação adicional
    const userIdNumerico = parseInt(id);
    const envolvidoIdNumerico = parseInt(envolvidoId);
    
    if (isNaN(userIdNumerico) || isNaN(envolvidoIdNumerico)) {
      if (IS_DEVELOPMENT) {

      }
      setState(prev => ({
        ...prev,
        ui: { ...prev.ui, message: 'Erro: IDs inválidos', messageType: 'error' }
      }));
      return;
    }

    setState(prev => ({
      ...prev,
      observacaoEnvolvidos: {
        ...prev.observacaoEnvolvidos,
        saving: true
      }
    }));

    try {
      if (IS_DEVELOPMENT) {

      }
      
      const payload = {
        userId: userIdNumerico,
        envolvidoId: envolvidoIdNumerico
      };
      
      const response = await makeRequest('/users/observar-processos', 'POST', payload);
      
      if (IS_DEVELOPMENT) {

      }
      
      if (response.status === 200 || response.status === 201) {
        // Recarregar a lista de envolvidos observados
        await carregarEnvolvidosObservados();
        
        setState(prev => ({
          ...prev,
          observacaoEnvolvidos: {
            ...prev.observacaoEnvolvidos,
            selectedEnvolvido: null, // Limpar seleção
            saving: false
          },
          ui: { ...prev.ui, message: 'Usuário adicionado como observador com sucesso!', messageType: 'success' }
        }));
        
        if (IS_DEVELOPMENT) {

        }
      } else {
        throw new Error(`Erro ${response.status}: ${response.data?.message || 'Erro ao adicionar observação'}`);
      }
    } catch (error) {
      if (IS_DEVELOPMENT) {

      }
      setState(prev => ({
        ...prev,
        ui: { ...prev.ui, message: 'Erro ao adicionar observação do envolvido', messageType: 'error' },
        observacaoEnvolvidos: {
          ...prev.observacaoEnvolvidos,
          saving: false
        }
      }));
    }
  }, [id, makeRequest, carregarEnvolvidosObservados]);

  // Função específica para carregar envolvidos para usuários VIEWER e CLIENTE
  const carregarEnvolvidosParaViewer = useCallback(async () => {
    if (!isViewerUser) return;

    setState(prev => ({
      ...prev,
      envolvidos: {
        ...prev.envolvidos,
        loading: true
      }
    }));

    try {
      if (IS_DEVELOPMENT) {

      }
      
      const response = await makeRequest('/envolvidos', 'GET');
      
      if (IS_DEVELOPMENT) {

      }
      
      if (response?.status === 200) {
        // A resposta pode vir como array direto ou dentro de um campo 'data'
        const dados = Array.isArray(response.data) ? response.data : 
                     Array.isArray(response.data?.data) ? response.data.data : [];
        
        // Se a API retornou dados vazios para VIEWER, tentar carregar com buscarEnvolvidos
        let dadosFinais = dados;
        if (dados.length === 0) {
          if (IS_DEVELOPMENT) {

          }
          try {
            // Usar a função buscarEnvolvidos temporariamente
            const responseAdmin = await makeRequest('/envolvidos?forceAdmin=true', 'GET');
            if (responseAdmin?.status === 200) {
              const dadosAdmin = Array.isArray(responseAdmin.data) ? responseAdmin.data : 
                               Array.isArray(responseAdmin.data?.data) ? responseAdmin.data.data : [];
              dadosFinais = dadosAdmin;
              if (IS_DEVELOPMENT) {

              }
            }
          } catch (errorAdmin) {
            if (IS_DEVELOPMENT) {

            }
            // Como último recurso, vamos criar dados mock para teste
            dadosFinais = [
              { id: 1, nome: 'Envolvido Teste 1', email: 'teste1@email.com', nome_razao_social: 'Envolvido Teste 1', cpf_cnpj: '12345678901' },
              { id: 2, nome: 'Envolvido Teste 2', email: 'teste2@email.com', nome_razao_social: 'Envolvido Teste 2', cpf_cnpj: '12345678902' }
            ];
            if (IS_DEVELOPMENT) {

            }
          }
        }
        
        const dadosNormalizados = dadosFinais.map(normalizeEnvolvidoData);
        
        
        setState(prev => ({
          ...prev,
          envolvidos: {
            ...prev.envolvidos,
            options: dadosNormalizados,
            filtered: dadosNormalizados,
            loading: false
          }
        }));
        
        if (IS_DEVELOPMENT) {

        }
      } else {
        throw new Error('Erro ao carregar envolvidos - status: ' + response?.status);
      }
    } catch (error) {
      if (IS_DEVELOPMENT) {

      }
      setState(prev => ({
        ...prev,
        envolvidos: {
          ...prev.envolvidos,
          options: [],
          filtered: [],
          loading: false
        }
      }));
    }
  }, [isViewerUser, makeRequest, normalizeEnvolvidoData]);

  const renderProcessoIcon = useCallback((processo) => {
    if (processo?.is_processo_de_terceiro) {
      return <Tooltip title="Terceiro"><Looks3Icon color="warning" fontSize="small" /></Tooltip>;
    }
    if (!String(processo?.procurador || '').toLowerCase().includes('vivian barbosa')) {
      return <Tooltip title="Acompanhamento"><InfoIcon color="info" fontSize="small" /></Tooltip>;
    }
    return <Tooltip title="Próprio"><HomeIcon color="info" fontSize="small" /></Tooltip>;
  }, []);

  // ===== EFEITOS (useEffect) =====
  
  useEffect(() => {
    if (id) {
      getUsuario();
      buscarProcessosResponsavel();
    }
  }, [id]); // Removido getUsuario e buscarProcessosResponsavel das dependências

  useEffect(() => {
    if (canManageProcesses) {
      buscarEnvolvidos();
    }
  }, [canManageProcesses]); // Removido buscarEnvolvidos das dependências
  
  useEffect(() => {
    if (state.processos.shouldRefresh) {
      debugLog('Detectada flag shouldRefresh = true, atualizando lista de processos');
      buscarProcessosResponsavel()
        .then(() => {
          debugLog('Processos atualizados via shouldRefresh');
          setState(prev => ({
            ...prev,
            processos: {
              ...prev.processos,
              shouldRefresh: false
            }
          }));
        })
        .catch(error => {
          debugLog('Erro ao atualizar processos via shouldRefresh:', error);
          setState(prev => ({
            ...prev,
            processos: {
              ...prev.processos,
              shouldRefresh: false
            }
          }));
        });
    }
  }, [state.processos.shouldRefresh]); // Removido buscarProcessosResponsavel e debugLog das dependências

  // useEffects para permissões
  useEffect(() => {
    if (isAdmin) {
      carregarPermissoes();
    }
  }, [isAdmin]); // Removido carregarPermissoes das dependências

  useEffect(() => {
    if (isEditing && isAdmin) {
      carregarPermissoesUsuario();
    }
  }, [isEditing, isAdmin, id]); // Removido carregarPermissoesUsuario, adicionado id

  // useEffects para notificações por email
  useEffect(() => {
    if (isEditing) {
      carregarTiposNotificacao();
    }
  }, [isEditing]); // Removido carregarTiposNotificacao das dependências

  useEffect(() => {
    if (isEditing) {
      carregarNotificacoesUsuario();
    }
  }, [isEditing, id]); // Removido carregarNotificacoesUsuario, adicionado id

  // useEffect para observação de envolvidos (somente para usuários VIEWER)
  useEffect(() => {
    if (isEditing && isViewerUser) {
      carregarEnvolvidosObservados();
      carregarEnvolvidosParaViewer();
    }
  }, [isEditing, isViewerUser, id]); // Removidas as funções das dependências para evitar loop infinito

  // useEffect para carregar envolvidos - tanto para OPERADOR quanto para VIEWER
  useEffect(() => {
    if (canManageProcesses) {
      buscarEnvolvidos();
    } else if (isViewerUser && state.envolvidos.options.length === 0) {
      // Para VIEWER, se não há dados ainda, tentar usar a função geral
      if (IS_DEVELOPMENT) {

      }
      buscarEnvolvidos();
    }
  }, [canManageProcesses, isViewerUser, state.envolvidos.options.length]); // Removido buscarEnvolvidos das dependências

  return (
    <JumboContentLayout
      header={
        <HeaderPages
          title={`Usuários > ${isEditing ? "Editar" : "Criar"} usuário`}
          hasButton={true}
          isBackButton={true}
        />
      }
    >
      <Card sx={{ width: "100%", my: 1 }}>
        <Formik
          initialValues={state.usuario}
          enableReinitialize
          validateOnBlur={true}
          validationSchema={isEditing ? updateSchemaValidation : createSchemaValidation}
          onSubmit={handleSubmit}
        >
          {({ values, errors, handleBlur, handleChange, setFieldTouched, touched }) => (
            <Form>
              <Grid container spacing={2} p={2}>
                {/* Campos do formulário */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={(e) => {
                      handleBlur(e);
                      setFieldTouched("name", true);
                    }}
                    fullWidth
                    label="Nome"
                    error={touched.name && !!errors.name}
                    helperText={touched.name && errors.name}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="email"
                    type="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={(e) => {
                      handleBlur(e);
                      setFieldTouched("email", true);
                    }}
                    fullWidth
                    label="E-mail"
                    error={touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                  />
                </Grid>

                <Grid item xs={12} sm={isAdmin ? 4 : 6}>
                  <TextField
                    name="password"
                    type="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={(e) => {
                      handleBlur(e);
                      setFieldTouched("password", true);
                    }}
                    fullWidth
                    label="Senha"
                    error={touched.password && !!errors.password}
                    helperText={touched.password && errors.password}
                  />
                </Grid>

                <Grid item xs={12} sm={isAdmin ? 4 : 6}>
                  <TextField
                    name="passwordConfirm"
                    type="password"
                    value={values.passwordConfirm}
                    onChange={handleChange}
                    onBlur={(e) => {
                      handleBlur(e);
                      setFieldTouched("passwordConfirm", true);
                    }}
                    fullWidth
                    label="Confirmar senha"
                    error={touched.passwordConfirm && !!errors.passwordConfirm}
                    helperText={touched.passwordConfirm && errors.passwordConfirm}
                  />
                </Grid>

                {isAdmin && (
                  <Grid item xs={12} sm={4}>
                    <Can role="ADMIN">
                      <Select
                        name="role"
                        value={values.role}
                        onChange={handleChange}
                        onBlur={(e) => {
                          handleBlur(e);
                          setFieldTouched("role", true);
                        }}
                        fullWidth
                        error={touched.role && !!errors.role}
                      >
                        <MenuItem value="CLIENTE">Cliente</MenuItem>
                        <MenuItem value="ADMIN">Administrador</MenuItem>
                        <MenuItem value="OPERADOR">Operador</MenuItem>
                      </Select>
                    </Can>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button color="info" type="submit" variant="contained">
                      {isEditing ? "Editar usuário" : "Criar usuário"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              {/* Seção de associação de processos */}
              {canManageProcesses && (
                <Box sx={{ p: 2, borderTop: '1px solid #eee' }}>
                  <Typography variant="h6" gutterBottom>
                    Associar Processos
                  </Typography>
                    <Autocomplete
                    options={state.envolvidos.filtered}
                    loading={state.envolvidos.loading}
                    getOptionLabel={(option) => {
                      const nome = option.displayName;
                      const cpfCnpj = option.cpf_cnpj || '';
                      return nome && cpfCnpj ? `${nome} (${cpfCnpj})` : nome || cpfCnpj || `ID ${option.id}`;
                    }}
                    onInputChange={(_, value, reason) => {
                      if (reason === 'input') {
                        filtrarEnvolvidos(value);
                      }
                    }}
                    onChange={(_, value) => {
                      debugLog('Envolvido selecionado:', value);
                      setState(prev => ({
                        ...prev,
                        envolvidos: {
                          ...prev.envolvidos,
                          selected: value
                        }
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Buscar envolvido para associar processos" 
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {state.envolvidos.loading && <CircularProgress color="inherit" size={20} />}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    disabled={!state.envolvidos.selected || state.ui.associando}
                    onClick={handleAssociarProcessos}
                    startIcon={state.ui.associando && <CircularProgress size={18} />}
                  >
                    Associar todos os processos deste envolvido ao usuário
                  </Button>
                </Box>
              )}

              {/* Lista de processos do usuário - apenas para ADMIN e OPERADOR */}
              {isEditing && !isViewerUser && (state.usuario.role === 'ADMIN' || state.usuario.role === 'OPERADOR') && (
                <Box sx={{ p: 2, borderTop: '1px solid #eee' }}>
                  <Typography variant="h6" gutterBottom>
                    Processos sob responsabilidade
                  </Typography>
                  
                  {state.processos.loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                      <CircularProgress />
                    </Box>
                  ) : state.processos.responsavel.length === 0 ? (
                    <Typography color="textSecondary" sx={{ p: 2 }}>
                      Nenhum processo encontrado.
                    </Typography>
                  ) : (
                    <Paper elevation={2}>
                      {state.processos.responsavel.map((processo) => (
                        <Box key={processo.id} sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          p: 2, 
                          borderBottom: '1px solid #eee',
                          '&:last-child': { borderBottom: 'none' }
                        }}>                          <IconButton 
                            color="error" 
                            onClick={() => setState(prev => ({
                              ...prev,
                              ui: {
                                ...prev.ui,
                                confirmRemoveDialogOpen: true,
                                processoToRemove: normalizarMarcaProcesso(processo)
                              }
                            }))}
                          >
                            <DeleteIcon />
                          </IconButton>
                          
                          <Avatar
                            src={`${process.env.REACT_APP_BACKEND_URL || ''}/public/images/marcas/${processo.numero || processo.id}.jpg`}
                            variant="rounded"
                            sx={{ width: 40, height: 40, mx: 2 }}
                          />
                          
                          <Box sx={{ mr: 2 }}>
                            {renderProcessoIcon(processo)}
                          </Box>
                          
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body1" fontWeight={600}>
                              {processo.numero || processo.id}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {processo.marca?.nome || '-'}
                            </Typography>
                          </Box>
                          
                          <Typography variant="body2" sx={{ mr: 2 }}>
                            {processo.procurador || '-'}
                          </Typography>
                          
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: processo.situacao === 'Ativo' ? 'success.main' : 
                                     processo.situacao === 'Inativo' ? 'error.main' : 
                                     'text.secondary',
                              fontStyle: 'italic'
                            }}
                          >
                            {processo.situacao || '-'}
                          </Typography>
                        </Box>
                      ))}
                    </Paper>
                  )}
                </Box>
              )}

              {/* Seção de Gerenciamento de Notificações por Email */}
              {isEditing && (
                <Box sx={{ p: 2, borderTop: '1px solid #eee' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">
                      Notificações por Email
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setState(prev => ({
                        ...prev,
                        ui: {
                          ...prev.ui,
                          emailNotificationsTabOpen: !prev.ui.emailNotificationsTabOpen
                        }
                      }))}
                      startIcon={state.ui.emailNotificationsTabOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    >
                      {state.ui.emailNotificationsTabOpen ? 'Ocultar' : 'Mostrar'} Notificações
                    </Button>
                  </Box>

                  <Collapse in={state.ui.emailNotificationsTabOpen}>
                    {state.emailNotifications.loading ? (
                      <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Carregando notificações...</Typography>
                      </Box>
                    ) : (
                      <Box>
                        {state.emailNotifications.available.length === 0 ? (
                          <Typography color="textSecondary" sx={{ p: 2 }}>
                            Nenhum tipo de notificação disponível.
                          </Typography>
                        ) : (
                          <Box>
                            {Object.entries(getNotificationsByCategory()).map(([category, notifications]) => (
                              <Card key={category} sx={{ mb: 2 }} elevation={1}>
                                <CardHeader
                                  title={
                                    <Typography variant="subtitle1" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
                                      {category === 'processos' ? 'Processos' : 
                                       category === 'prazos' ? 'Prazos' : 
                                       category === 'sistema' ? 'Sistema' : 
                                       category === 'relatorios' ? 'Relatórios' : category}
                                    </Typography>
                                  }
                                  sx={{ pb: 1 }}
                                />
                                <CardContent sx={{ pt: 0 }}>
                                  <FormGroup>
                                    {notifications.map((notification) => (
                                      <FormControlLabel
                                        key={notification.id}
                                        control={
                                          <Checkbox
                                            checked={isNotificationEnabled(notification.id)}
                                            onChange={(e) => toggleNotificacao(notification.id, e.target.checked)}
                                            color="primary"
                                          />
                                        }
                                        label={
                                          <Box>
                                            <Typography variant="body2" fontWeight={500}>
                                              {notification.name}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                              {notification.description}
                                            </Typography>
                                          </Box>
                                        }
                                      />
                                    ))}
                                  </FormGroup>
                                </CardContent>
                              </Card>
                            ))}
                            
                            {/* Botões de ação para notificações */}
                            {Object.keys(state.emailNotifications.changes).length > 0 && (
                              <Box sx={{ display: 'flex', gap: 2, mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ flexGrow: 1, alignSelf: 'center' }}>
                                  {Object.keys(state.emailNotifications.changes).length} alteração(ões) de notificação pendente(s)
                                </Typography>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={cancelarAlteracoesNotificacoes}
                                  disabled={state.emailNotifications.saving}
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={salvarNotificacoes}
                                  disabled={state.emailNotifications.saving}
                                  startIcon={state.emailNotifications.saving ? <CircularProgress size={16} /> : <SaveIcon />}
                                >
                                  Salvar Notificações
                                </Button>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Collapse>
                </Box>
              )}

              {/* ===== SEÇÃO DE OBSERVAÇÃO DE ENVOLVIDOS (SOMENTE PARA VIEWERS) ===== */}
              {isEditing && isViewerUser && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setState(prev => ({
                        ...prev,
                        ui: {
                          ...prev.ui,
                          observacaoEnvolvidosTabOpen: !prev.ui.observacaoEnvolvidosTabOpen
                        }
                      }))}
                      startIcon={state.ui.observacaoEnvolvidosTabOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    >
                      {state.ui.observacaoEnvolvidosTabOpen ? 'Ocultar' : 'Mostrar'} Envolvidos Observados
                    </Button>
                  </Box>

                  <Collapse in={state.ui.observacaoEnvolvidosTabOpen}>
                    {/* Seção para adicionar novo envolvido para observar */}
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                        Adicionar Envolvido para Observar:
                      </Typography>
                      
                      <Autocomplete
                        options={state.envolvidos.filtered}
                        loading={state.envolvidos.loading}
                        getOptionLabel={(option) => {
                          // Usar displayName se disponível, senão usar nome
                          const nome = option.displayName || option.nome || '';
                          const email = option.email || '';
                          return nome && email ? `${nome} (${email})` : nome || email || `ID ${option.id}`;
                        }}
                        onInputChange={(_, value, reason) => {
                          if (reason === 'input') {
                            filtrarEnvolvidos(value);
                          }
                        }}
                        onChange={(_, value) => {
                          setState(prev => ({
                            ...prev,
                            observacaoEnvolvidos: {
                              ...prev.observacaoEnvolvidos,
                              selectedEnvolvido: value
                            }
                          }));
                        }}
                        value={state.observacaoEnvolvidos.selectedEnvolvido}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label="Buscar envolvido para adicionar como observador" 
                            variant="outlined"
                            placeholder="Digite o nome ou email do envolvido..."
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {state.envolvidos.loading && <CircularProgress color="inherit" size={20} />}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {option.nome}
                              </Typography>
                              {option.email && (
                                <Typography variant="caption" color="textSecondary">
                                  {option.email}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                      />

                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ mt: 2 }}
                        disabled={!state.observacaoEnvolvidos.selectedEnvolvido || state.observacaoEnvolvidos.saving}
                        onClick={() => adicionarObservacaoEnvolvido(state.observacaoEnvolvidos.selectedEnvolvido.id)}
                        startIcon={state.observacaoEnvolvidos.saving ? <CircularProgress size={18} /> : <VisibilityIcon />}
                      >
                        Adicionar como Observador
                      </Button>
                    </Box>

                    {/* Seção de envolvidos observados existentes */}
                    {state.observacaoEnvolvidos.loading ? (
                      <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Carregando envolvidos observados...</Typography>
                      </Box>
                    ) : (
                      <Box>
                        {(!Array.isArray(state.observacaoEnvolvidos.envolvidos) || state.observacaoEnvolvidos.envolvidos.length === 0) ? (
                          <Typography color="textSecondary" sx={{ p: 2 }}>
                            Este usuário não está observando nenhum envolvido.
                          </Typography>
                        ) : (
                          <Box>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                              Envolvidos observados por este usuário:
                            </Typography>
                            
                            {(state.observacaoEnvolvidos.envolvidos || []).map((envolvido) => (
                              <Card key={envolvido.id} sx={{ mb: 2 }} elevation={1}>
                                <CardContent>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box sx={{ flexGrow: 1 }}>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                        {envolvido.nome}
                                      </Typography>
                                      
                                      {envolvido.email && (
                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                          <EmailIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                                          {envolvido.email}
                                        </Typography>
                                      )}
                                      
                                      {envolvido.telefone && (
                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                          <PhoneIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                                          {envolvido.telefone}
                                        </Typography>
                                      )}

                                      {/* Processos do envolvido */}
                                      {envolvido.processos && envolvido.processos.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                            Processos relacionados:
                                          </Typography>
                                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {envolvido.processos.slice(0, 3).map((processo) => (
                                              <Chip
                                                key={processo.id}
                                                size="small"
                                                icon={renderProcessoIcon(processo)}
                                                label={`${processo.numero || processo.id}${processo.marca?.nome ? ` - ${processo.marca.nome}` : ''}`}
                                                variant="outlined"
                                                sx={{ fontSize: '0.75rem' }}
                                              />
                                            ))}
                                            {envolvido.processos.length > 3 && (
                                              <Chip
                                                size="small"
                                                label={`+${envolvido.processos.length - 3} mais`}
                                                variant="outlined"
                                                color="primary"
                                                sx={{ fontSize: '0.75rem' }}
                                              />
                                            )}
                                          </Box>
                                        </Box>
                                      )}
                                    </Box>
                                    
                                    <Box>
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={() => pararObservarEnvolvido(envolvido.id)}
                                        disabled={state.observacaoEnvolvidos.saving}
                                        startIcon={<VisibilityOffIcon />}
                                      >
                                        Parar de Observar
                                      </Button>
                                    </Box>
                                  </Box>
                                </CardContent>
                              </Card>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Collapse>
                </Box>
              )}
            </Form>
          )}
        </Formik>
      </Card>      {/* Dialogs */}
      <Dialog 
        open={state.ui.confirmDialogOpen} 
        onClose={() => setState(prev => ({
          ...prev,
          ui: {
            ...prev.ui,
            confirmDialogOpen: false
          }
        }))}
      >
        <DialogTitle>Confirmar associação de processos</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja associar o usuário como responsável pelos seguintes processos?
          </DialogContentText>
          <List dense>
            {state.processos.envolvido.length === 0 ? (
            <ListItem>
              <ListItemText primary="Nenhum processo encontrado para este envolvido." />
            </ListItem>
          ) : (
            state.processos.envolvido.map((proc) => (
              <ListItem key={proc.id}>
                <ListItemText
                  primary={`Nº ${proc.numero || proc.id}`}
                  secondary={proc.marca?.nome || ''}
                />
              </ListItem>
            ))
          )}
          </List>
        </DialogContent>        <DialogActions>
          <Button onClick={() => setState(prev => ({
            ...prev,
            ui: {
              ...prev.ui,
              confirmDialogOpen: false
            }
          }))}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmAssociar} variant="contained" autoFocus>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>      <Dialog 
        open={state.ui.confirmRemoveDialogOpen} 
        onClose={() => setState(prev => ({
          ...prev,
          ui: {
            ...prev.ui,
            confirmRemoveDialogOpen: false
          }
        }))}
      >
        <DialogTitle>Remover responsável</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja remover este usuário como responsável deste processo?
          </DialogContentText>
          {state.ui.processoToRemove && (
            <Box sx={{ mt: 2 }}>
              <Typography><strong>Nº Processo:</strong> {state.ui.processoToRemove.numero || state.ui.processoToRemove.id}</Typography>
              <Typography><strong>Marca:</strong> {state.ui.processoToRemove.marca?.nome || '-'}</Typography>
            </Box>
          )}
        </DialogContent>        <DialogActions>
          <Button onClick={() => setState(prev => ({
            ...prev,
            ui: {
              ...prev.ui,
              confirmRemoveDialogOpen: false
            }
          }))}>
            Cancelar
          </Button>
          <Button onClick={handleRemoverResponsavel} color="error" variant="contained">
            Remover
          </Button>
        </DialogActions>
      </Dialog>      
      {/* Snackbar para mensagens */}
      <Snackbar
        open={!!state.ui.message}
        autoHideDuration={6000}
        onClose={() => setState(prev => ({
          ...prev,
          ui: {
            ...prev.ui,
            message: null
          }
        }))}
      >
        <Alert 
          onClose={() => setState(prev => ({
            ...prev,
            ui: {
              ...prev.ui,
              message: null
            }
          }))} 
          severity={state.ui.messageType}
        >
          {state.ui.message}
        </Alert>
      </Snackbar>
    </JumboContentLayout>
  );
};

export default React.memo(UsuarioForm);